import {
  apiWriteCreate,
  type ApiFetchOptions,
  type CreateResult,
  type SifaApiConfig,
} from '../client.js';

/** Body accepted by {@link createEndorsement}. */
export interface EndorsementInput {
  /** DID of the person being endorsed. */
  subjectDid: string;
  /**
   * AT-URI of the subject's `id.sifa.profile.skill` record. Omit to propose a
   * skill they have not listed; `skillName` then names what is proposed, and
   * the record is created when they accept.
   */
  skillUri?: string;
  /**
   * CID of that skill record, pinning the endorsement to the version endorsed.
   * Optional: the AppView resolves it when omitted, which is the common case
   * since only the firehose carries a CID. Never substitute another record's.
   */
  skillCid?: string;
  /**
   * Snapshot of the skill's name at endorsement time. Acts as the validity
   * anchor: if the subject later renames the skill, the mismatch is detectable.
   */
  skillName: string;
  comment?: string;
}

/** Body accepted by {@link confirmEndorsement}. */
export interface ConfirmEndorsementResult {
  /** Skill the endorsement resolved to, created if it was a proposal. */
  skillUri?: string;
  /** True when accepting added a new skill to the profile. */
  skillCreated?: boolean;
}

export interface ConfirmEndorsementInput {
  endorsementUri: string;
  /**
   * Optional. When omitted the AppView resolves the CID itself, reading the
   * record from the endorser's PDS if it has none stored. Callers working from
   * the pending inbox should pass `cid` through when it is there and leave it
   * out when it is not, rather than substituting another record's CID.
   */
  endorsementCid?: string;
}

/**
 * Create an endorsement of another user's skill. The endorsed user
 * must confirm before the endorsement appears on their profile (the
 * endorsement record is on the endorser's PDS; a separate confirmation
 * record on the endorsed user's PDS gates display).
 */
export function createEndorsement(
  config: SifaApiConfig,
  data: EndorsementInput,
  options: ApiFetchOptions = {},
): Promise<CreateResult> {
  return apiWriteCreate(config, '/api/endorsement', data, options);
}

/**
 * Confirm a received endorsement, writing a confirmation record to the
 * signed-in user's PDS. This is what makes the endorsement public: until the
 * confirmation exists, the endorsement is indexed but displays nowhere.
 */
export function confirmEndorsement(
  config: SifaApiConfig,
  data: ConfirmEndorsementInput,
  options: ApiFetchOptions = {},
): Promise<CreateResult & ConfirmEndorsementResult> {
  return apiWriteCreate<ConfirmEndorsementResult>(
    config,
    '/api/endorsement/confirm',
    data,
    options,
  );
}
